
import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Info, Users, Target, Award, ArrowRight } from 'lucide-react';
import { images } from '@/assets/images';
import SEOHead from '@/components/seo/SEOHead';
import SEOFooter from '@/components/layout/SEOFooter';

const About: React.FC = () => {
  const features = [
    {
      title: 'Advanced Calculators',
      description: 'Sophisticated mortgage and buying power calculators with real-time market data'
    },
    {
      title: 'Smart Analytics',
      description: 'AI-powered insights and recommendations for smarter real estate decisions'
    },
    {
      title: 'Market Intelligence',
      description: 'Comprehensive market analysis and neighborhood insights'
    },
    {
      title: 'Professional Tools',
      description: 'Enterprise-grade tools for real estate professionals and investors'
    }
  ];

  const team = [
    {
      name: 'Development Team',
      role: 'Engineering & Product',
      description: 'Expert developers and product managers building cutting-edge real estate technology'
    },
    {
      name: 'Data Science Team',
      role: 'Analytics & AI',
      description: 'Data scientists and AI specialists creating intelligent market insights'
    },
    {
      name: 'Real Estate Experts',
      role: 'Industry Knowledge',
      description: 'Licensed professionals providing industry expertise and validation'
    }
  ];

  return (
    <div className="container mx-auto px-4 py-8 space-y-8">
      <SEOHead
        title="About EquiForge — AI-Powered Real Estate Intelligence Platform"
        description="Learn about EquiForge, the AI-powered real estate analytics and mortgage calculator platform built for homebuyers and real estate professionals."
        keywords="real estate platform, mortgage tools, property analysis, AI real estate, homebuyer tools, EquiForge about"
        canonicalPath="/about"
      />
      <div className="flex justify-center mb-6">
        <Link to="/dashboard" className="hover:scale-105 transition-transform duration-300">
          <img
            src={images.equiforgeLogoFlat}
            alt="EquiForge"
            className="h-10 md:h-12 lg:h-14 w-auto object-contain drop-shadow-md"
          />
        </Link>
      </div>

      <div className="text-center">
        <div className="flex items-center justify-center gap-2 mb-4">
          <Info className="w-8 h-8 text-blue-600" />
          <h1 className="text-4xl font-bold">About EquiForge</h1>
        </div>
        <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
          Empowering smarter real estate decisions through advanced technology and data-driven insights
        </p>
      </div>

      {/* Mission Statement */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="w-6 h-6 text-blue-600" />
            Our Mission
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-lg leading-relaxed">
            At EquiForge, we believe that everyone deserves access to professional-grade real estate tools and insights. 
            Our mission is to democratize real estate intelligence by providing advanced calculators, market analytics, 
            and decision-making tools that were previously only available to industry professionals.
          </p>
        </CardContent>
      </Card>

      {/* What We Do */}
      <Card>
        <CardHeader>
          <CardTitle>What We Do</CardTitle>
          <CardDescription>
            Comprehensive real estate technology solutions for buyers, sellers, and professionals
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-6">
            {features.map((feature, index) => (
              <div key={index} className="flex gap-4">
                <div className="w-2 h-2 bg-blue-600 rounded-full mt-3 flex-shrink-0"></div>
                <div>
                  <h3 className="font-semibold mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground text-sm">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Our Values */}
      <div className="grid md:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Accuracy</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              We use the latest market data and proven algorithms to ensure our calculations and insights are precise and reliable.
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Accessibility</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Professional real estate tools shouldn't be exclusive. We make advanced analytics accessible to everyone.
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Innovation</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              We continuously push the boundaries of what's possible in real estate technology and user experience.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Team */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-6 h-6 text-blue-600" />
            Our Team
          </CardTitle>
          <CardDescription>
            Bringing together expertise from technology, data science, and real estate
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-3 gap-6">
            {team.map((member, index) => (
              <div key={index} className="text-center p-4 border rounded-lg">
                <h3 className="font-semibold mb-1">{member.name}</h3>
                <Badge variant="secondary" className="mb-3">{member.role}</Badge>
                <p className="text-sm text-muted-foreground">{member.description}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Recognition */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Award className="w-6 h-6 text-blue-600" />
            Recognition & Trust
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="font-semibold mb-3">Industry Standards</h3>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                  FNMA/FHLMC compliant calculations
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                  FHA/VA/USDA program requirements
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                  Real-time market data integration
                </li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-3">Security & Privacy</h3>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                  Bank-level data encryption
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                  GDPR & CCPA compliant
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                  No personal data sharing
                </li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Call to Action */}
      <Card className="bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
        <CardContent className="text-center py-8">
          <h2 className="text-2xl font-bold mb-4 text-gray-900">Ready to Get Started?</h2>
          <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
            Join thousands of users who trust EquiForge for their real estate calculations and market insights.
          </p>
          <div className="flex gap-4 justify-center">
            <Button asChild size="lg" className="bg-blue-600 hover:bg-blue-700">
              <Link to="/calculator">
                Try Our Calculator
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/pricing">
                View Plans
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>
      <SEOFooter />
    </div>
  );
};

export default About;
